/**
 * CharnelGroundSkillEffect — Poison tree plague path active (Fork 2).
 *
 * Places a 15-second zone marker at a targeted position. Any enemy killed
 * within the marker's 4-tile radius — by any damage source whatsoever —
 * triggers a Carrier-equivalent `carrier_cloud` toxic zone at the death
 * position. Cloud duration scales with remaining `poison` DoT on the dying
 * enemy; kills without poison still produce a minimum-duration cloud (3s).
 *
 * The mark itself is non-damaging and non-interactable — a faint circle on
 * the ground whose sole purpose is to feed the DeathTriggerSystem. When the
 * mark expires, its death trigger is unregistered and no further clouds spawn.
 *
 * Implementation: on activate, creates a `charnel_ground` zone via ZoneSystem
 * and registers a DeathTriggerConfig with `requireInZone: 'charnel_ground'`.
 * The zone's `onExpiry` callback unregisters the trigger. Only one mark can
 * be active at a time — a new activation replaces the previous one.
 *
 * Upgrades: Overripe and Drawn Inward both `enhancesId: 'charnel_ground'`
 * and add resolver keys that the integration layer processes on trigger fire.
 * Dormancy (path passive) modifies carrier cloud visibility independently.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see poison-spec.md § Charnel Ground (charnel_ground)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { ZoneConfig } from '../../../systems/ZoneSystem.js';
import type { DeathTriggerConfig } from '../../../systems/DeathTriggerSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface CharnelGroundDeps {
  readonly zoneSystem: {
    create(config: ZoneConfig): string;
  };
  readonly deathTriggers: {
    register(config: DeathTriggerConfig): void;
    unregister(triggerId: string): void;
  };
  readonly targeting: {
    getTargetPosition(): { x: number; y: number };
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface CharnelGroundConfig {
  /** Cooldown in ms (long tier = 2000). */
  readonly cooldownMs: number;
  /** Mark zone radius in tiles. */
  readonly markRadiusTiles: number;
  /** Mark zone duration in ms. */
  readonly markDurationMs: number;
  /** Carrier-cloud radius in tiles (matching Carrier's 1.5). */
  readonly cloudRadiusTiles: number;
  /** Minimum cloud duration in ms — used when dying enemy has no poison. */
  readonly minCloudDurationMs: number;
}

const DEFAULT_CONFIG: CharnelGroundConfig = {
  cooldownMs: 2000,       // long tier
  markRadiusTiles: 4,
  markDurationMs: 15000,
  cloudRadiusTiles: 1.5,
  minCloudDurationMs: 3000,
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MARK_ZONE_TYPE = 'charnel_ground';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class CharnelGroundSkillEffect implements SkillEffect {
  readonly skillId = 'charnel_ground';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: CharnelGroundDeps;
  private readonly config: CharnelGroundConfig;

  private _cooldownRemaining = 0;
  private _activationsCount = 0;
  private _activeMarkZoneId: string | null = null;
  private _activeTriggerId: string | null = null;

  constructor(deps: CharnelGroundDeps, config: Partial<CharnelGroundConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationsCount(): number { return this._activationsCount; }
  get activeMarkZoneId(): string | null { return this._activeMarkZoneId; }
  get activeTriggerId(): string | null { return this._activeTriggerId; }

  /**
   * Charnel Ground clouds always scale duration with the dying enemy's
   * remaining poison DoT. Unlike Carrier (which gates this behind a
   * resolver boolean), Charnel Ground's scaling is intrinsic — the
   * integration layer reads this flag to substitute the static durationMs
   * with the remaining DoT time. When no poison is present, the base
   * `minCloudDurationMs` serves as the fallback.
   */
  get scalesWithRemainingDot(): boolean { return true; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const target = this.deps.targeting.getTargetPosition();
    const markIndex = this._activationsCount;
    const markId = `charnel_mark_${markIndex}`;
    const triggerId = `charnel_death_${markIndex}`;

    // Replace any previously active mark + trigger
    this.cleanupMark();

    // Create the non-damaging zone marker — no DoT, no movement effects,
    // purely a spatial anchor for the death trigger's requireInZone check.
    this.deps.zoneSystem.create({
      id: markId,
      type: MARK_ZONE_TYPE,
      position: { x: target.x, y: target.y },
      durationMs: this.config.markDurationMs,
      sourceSkillId: 'charnel_ground',
      shape: 'circle',
      radiusTiles: this.config.markRadiusTiles,
      onExpiry: () => this.cleanupMark(),
    });

    // Register the death trigger — fires for ANY kill inside the zone,
    // regardless of damage source or poison status.
    this.deps.deathTriggers.register({
      id: triggerId,
      sourceSkillId: 'charnel_ground',
      requireInZone: MARK_ZONE_TYPE,
      spawnZone: {
        zoneType: 'carrier_cloud',
        radiusTiles: this.config.cloudRadiusTiles,
        durationMs: this.config.minCloudDurationMs,
      },
    });

    this._activeMarkZoneId = markId;
    this._activeTriggerId = triggerId;
    this._cooldownRemaining = this.config.cooldownMs;
    this._activationsCount++;
    return true;
  }

  deactivate(): void {
    // Instant activation — no toggle state
  }

  isActive(): boolean {
    return this._activeMarkZoneId !== null;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this.cleanupMark();
    this._cooldownRemaining = 0;
  }

  // --- Internal ---

  private cleanupMark(): void {
    if (this._activeTriggerId !== null) {
      this.deps.deathTriggers.unregister(this._activeTriggerId);
      this._activeTriggerId = null;
    }
    this._activeMarkZoneId = null;
  }
}
